import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pollsApi } from '../services/api';
import type { CreatePollInput, PollRespondRequest, Pagination } from '../types';

export function usePolls(page = 1, limit = 10) {
  return useQuery({
    queryKey: ['polls', page, limit],
    queryFn: async () => {
      const response = await pollsApi.getAll(page, limit);
      return {
        polls: response.data,
        pagination: response.pagination as Pagination,
      };
    },
    refetchInterval: 10000, // Auto-refresh every 10 seconds
  });
}

export function usePoll(id: number) {
  return useQuery({
    queryKey: ['polls', id],
    queryFn: async () => {
      const response = await pollsApi.getById(id);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreatePoll() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePollInput) => pollsApi.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['polls'] });
    },
  });
}

export function usePollSharedRespondInfo(pollToken: string | undefined) {
  return useQuery({
    queryKey: ['pollRespond', pollToken],
    queryFn: async () => {
      const response = await pollsApi.getSharedRespondInfo(pollToken as string);
      return response.data;
    },
    enabled: !!pollToken,
  });
}

export function usePollRespondInfo(pollToken: string | undefined, responseToken: string | undefined) {
  return useQuery({
    queryKey: ['pollRespond', pollToken, responseToken],
    queryFn: async () => {
      const response = await pollsApi.getRespondInfo(pollToken as string, responseToken as string);
      return response.data;
    },
    enabled: !!pollToken && !!responseToken,
  });
}

export function useSubmitPollResponse() {
  return useMutation({
    mutationFn: ({ pollToken, responseToken, data }: { pollToken: string; responseToken: string; data: PollRespondRequest }) =>
      pollsApi.submitResponse(pollToken, responseToken, data),
  });
}
